import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { cn } from "core/shadcn/lib/utils";

export interface SearchInputProps {
  onSearch: (value: string) => void;
  placeholder: string;
  value: string;
  className?: string;
  inputClassName?: string;
  compact?: boolean;
  noMargin?: boolean;
  width?: string;
  flexGrow?: string;
  dataTest?: string;
  "data-test"?: string;
  disabled?: boolean;
  "aria-label"?: string;
}

export function SearchInput({
  onSearch,
  placeholder,
  value,
  className,
  inputClassName,
  compact,
  noMargin,
  width,
  flexGrow,
  dataTest,
  "data-test": testId = dataTest,
  disabled,
  "aria-label": ariaLabel = placeholder,
}: SearchInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const search = useDebouncedCallback(onSearch, 400);

  useEffect(() => {
    setLocalValue(value);
    // Resets and unmounting must not emit a stale search draft.
    return () => search.cancel();
  }, [search, value]);

  useEffect(() => {
    if (disabled) search.cancel();
  }, [disabled, search]);

  return (
    <search
      aria-label={ariaLabel}
      className={cn(
        "relative block w-[204px] min-w-[150px] rounded-[4px] border border-solid border-input",
        {
          "h-6": compact,
          "h-9": !compact,
          "m-0": noMargin,
          "mx-4 my-3": !noMargin,
        },
        className,
      )}
      data-search-input=""
      style={{
        flexGrow,
        width,
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 z-[1] flex aspect-square items-center justify-center text-[#5f6b7c]"
      >
        {/* Keep the existing filled search glyph for exact caller parity. */}
        <svg
          className="block opacity-60"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M15.55 13.43l-2.67-2.68a6.94 6.94 0 001.11-3.76c0-3.87-3.13-7-7-7s-7 3.13-7 7 3.13 7 7 7c1.39 0 2.68-.42 3.76-1.11l2.68 2.67a1.498 1.498 0 102.12-2.12zm-8.56-1.44c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
        </svg>
      </span>
      <input
        aria-label={ariaLabel}
        className={cn(
          "relative !box-border h-full w-full !appearance-none !rounded-[30px] border-0 bg-transparent py-0 pr-[10px] align-middle !text-sm !leading-[30px] !font-normal !text-[#1c2127] !shadow-none placeholder:!text-[#5f6b7c] placeholder:!opacity-100 focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-sb-grey-500 disabled:cursor-not-allowed disabled:opacity-60",
          inputClassName,
        )}
        data-test={testId}
        disabled={disabled}
        onChange={(event) => {
          const draft = event.target.value;
          setLocalValue(draft);
          search(draft);
        }}
        placeholder={placeholder}
        style={{
          paddingLeft: compact ? 28 : 38,
        }}
        type="search"
        value={localValue ?? ""}
      />
    </search>
  );
}
